import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { X, Lock, ShieldCheck, Mail, ArrowLeft, KeyRound } from 'lucide-react';
import { authApi } from '../../utils/api';
import { useSiteSettings } from '../../hooks/useSiteSettings';

// ============================================================
// Two-step admin login popup:
//   STEP 1 — मोडल पासवर्ड (gate) → verify-modal
//   STEP 2 — इमेल + पासवर्ड (admin account) → login
// दुवै password field hidden (••••) — modal pw ra account pw FERAK hunuchha
// ============================================================
function AdminLogin({ onSuccess, onClose }) {
  const [step, setStep] = useState(1); // 1 = gate pw, 2 = email + pw
  const [gatePw, setGatePw] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const settings = useSiteSettings();

  // Modal kholeko bela body scroll band garne
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // STEP 1 — gate password verify
  const submitGate = async () => {
    if (!gatePw) {
      setError('पासवर्ड प्रविष्ट गर्नुहोस्।');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await authApi.verifyModal(gatePw);
      setStep(2);
      setGatePw('');
    } catch (err) {
      setError(err.response?.data?.error || 'गलत पासवर्ड।');
    } finally {
      setLoading(false);
    }
  };

  // STEP 2 — email + password login
  const submitLogin = async () => {
    if (!email || !password) {
      setError('इमेल र पासवर्ड दुवै आवश्यक छ।');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await authApi.login(email.trim(), password);
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminData', JSON.stringify(response.data.admin));
      toast.success('लगइन सफल भयो।');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'लगइन असफल भयो। कृपया पुनः प्रयास गर्नुहोस्।');
    } finally {
      setLoading(false);
    }
  };

  const onEnter = (fn) => (e) => {
    if (e.key === 'Enter') fn();
  };

  const inputCls =
    'w-full border border-line rounded-lg px-3 py-2.5 text-sm font-normal text-ink outline-none bg-white focus:border-crimson';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Popup modal card */}
      <div className="relative bg-white rounded-2xl shadow-2xl border border-line w-full max-w-[400px] overflow-hidden">
        {/* Top green brand strip */}
        <div className="relative bg-emerald-700 text-white px-6 pt-7 pb-6 text-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-800/50 to-emerald-600/50" />
          <button
            className="absolute top-3 right-3 z-20 inline-flex items-center justify-center w-9 h-9 rounded-[10px] bg-white/10 hover:bg-white/25 text-white transition-colors"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="relative z-10 flex flex-col items-center gap-3">
            {settings.header_logo ? (
              <img src={settings.header_logo} alt="Kalo Jhola" className="h-12 w-auto object-contain" />
            ) : (
              <span className="font-display text-[22px] font-bold tracking-tight">
                KALO<em className="not-italic text-gold">JHOLA</em>
              </span>
            )}
            <div className="w-14 border-t border-white/30" />
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 pt-4">
          {[1, 2].map((n) => (
            <span
              key={n}
              className={`h-1.5 rounded-full transition-all ${
                step === n ? 'w-8 bg-emerald-700' : 'w-3 bg-line'
              }`}
            />
          ))}
        </div>

        {/* STEP 1 — मोडल पासवर्ड */}
        {step === 1 && (
          <div className="p-6 sm:p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h3 className="font-display text-[19px] text-ink leading-tight">एडमिन लगइन</h3>
                <p className="text-[12px] text-muted">पहुँचका लागि पासवर्ड प्रविष्ट गर्नुहोस्।</p>
              </div>
            </div>

            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft">
              <span className="inline-flex items-center gap-1.5">
                <Lock size={13} /> पासवर्ड
              </span>
              <input
                type="password"
                autoComplete="off"
                className={`${inputCls} tracking-widest`}
                value={gatePw}
                onChange={(e) => setGatePw(e.target.value)}
                onKeyDown={onEnter(submitGate)}
                placeholder="••••••••"
                autoFocus
              />
            </label>

            {error && <div className="text-[12.5px] text-crimson font-semibold mt-2">{error}</div>}

            <button
              type="button"
              className="w-full mt-4 inline-flex items-center justify-center gap-2 bg-emerald-700 text-white font-semibold text-[13.5px] px-5 py-2.5 rounded-full hover:bg-emerald-800 disabled:opacity-60 transition-colors"
              onClick={submitGate}
              disabled={loading}
            >
              <KeyRound size={14} />
              {loading ? 'जाँच हुँदै...' : 'पुष्टि गर्नुहोस्'}
            </button>

            <p className="mt-3 text-center text-[11px] text-muted">
              सुरक्षित क्षेत्र — अधिकृत व्यक्तिले मात्र प्रवेश गर्नुहोस्।
            </p>
          </div>
        )}

        {/* STEP 2 — इमेल + पासवर्ड */}
        {step === 2 && (
          <div className="p-6 sm:p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
                <Mail size={20} />
              </div>
              <div>
                <h3 className="font-display text-[19px] text-ink leading-tight">एडमिन खाता</h3>
                <p className="text-[12px] text-muted">आफ्नो इमेल र पासवर्ड प्रविष्ट गर्नुहोस्।</p>
              </div>
            </div>

            <div className="flex flex-col gap-3.5">
              <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft">
                इमेल
                <input
                  type="email"
                  autoComplete="username"
                  className={inputCls}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={onEnter(submitLogin)}
                  placeholder="a@gmail.com"
                  autoFocus
                />
              </label>
              <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft">
                पासवर्ड
                <input
                  type="password"
                  autoComplete="current-password"
                  className={`${inputCls} tracking-widest`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={onEnter(submitLogin)}
                  placeholder="••••••••"
                />
              </label>
              {error && <div className="text-[12.5px] text-crimson font-semibold">{error}</div>}
              <button
                type="button"
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-700 text-white font-semibold text-[13.5px] px-5 py-2.5 rounded-full hover:bg-emerald-800 disabled:opacity-60 transition-colors"
                onClick={submitLogin}
                disabled={loading}
              >
                <Lock size={14} />
                {loading ? 'लगइन हुँदै...' : 'लगइन गर्नुहोस्'}
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center gap-1.5 text-[12.5px] font-semibold text-muted hover:text-ink transition-colors"
                onClick={() => {
                  setStep(1);
                  setEmail('');
                  setPassword('');
                  setError('');
                }}
              >
                <ArrowLeft size={13} /> पछाडि जानुहोस्
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminLogin;
